import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Archive, ArrowDownUp, Handshake, Merge, Plus, Tag, Users, X } from "lucide-react";
import type { ContactSort } from "@hub/shared";
import { Button, EmptyState, Menu, Select, Spinner, toast } from "@/ui";
import { useEngineMutation, useEngineQuery } from "@/engine/hooks";
import { useOpenContact, useSelection } from "@/app/navigation";
import { isTypingTarget } from "@/app/shortcuts";
import { Page } from "@/app/layout/Page";
import { CONTACT_DRAG_TYPE } from "@/app/layout/NavSidebar";
import { SearchBox } from "./SearchBox";
import { FilterPanel, useGroupNames } from "./FilterPanel";
import { ChipRow, useQueryChips } from "./ChipRow";
import { SORTS, clearPart, composeQuery, emptyFilter, filterParts, type PeopleFilter } from "./filter";
import { ContactRowView } from "./ContactRowView";
import { useContactActions } from "@/features/contact/ContactActionHost";
import { GroupPickerModal } from "@/features/contact/GroupPickerModal";
import { MergeModal } from "@/features/contact/MergeModal";
import { ConfirmModal } from "@/features/contact/ConfirmModal";
import s from "../shared.module.css";

const PAGE = 200;
const BANNER_KEY = "hub_needs_offers_banner_dismissed";

function readDismissed(): boolean {
  try {
    return localStorage.getItem(BANNER_KEY) === "1";
  } catch {
    return false;
  }
}

/** Shown until the user has at least one need or offer of their own, or dismisses it. */
function NeedsOffersBanner() {
  const [dismissed, setDismissed] = useState(readDismissed);
  const self = useEngineQuery("getSelfContact", undefined);
  if (dismissed || !self.isSuccess) return null;
  const has = self.data ? self.data.needs.some((n) => !n.isCompleted) || self.data.services.some((sv) => sv.isActive) : false;
  if (has) return null;
  const dismiss = () => {
    try {
      localStorage.setItem(BANNER_KEY, "1");
    } catch {
      // storage unavailable: dismiss for this session only
    }
    setDismissed(true);
  };
  return (
    <div className="u-row" style={{ gap: 10, padding: "8px 14px", background: "var(--accent-soft)", borderBottom: "1px solid var(--border)", flexWrap: "wrap" }} data-testid="needs-offers-banner">
      <Handshake size={15} style={{ color: "var(--accent)", flex: "none" }} />
      <span className="u-grow u-small" style={{ minWidth: 200 }}>Hub matches needs with offers across your network. Add yours.</span>
      <Link to="/needs?new=1" className="u-small" style={{ fontWeight: 600 }}>Add a need</Link>
      <Link to="/services?new=1" className="u-small" style={{ fontWeight: 600 }}>Add an offer</Link>
      <button type="button" onClick={dismiss} aria-label="Dismiss" title="Dismiss" style={{ border: "none", background: "transparent", cursor: "pointer", color: "var(--text-tertiary)", display: "inline-flex", padding: 2 }}><X size={14} /></button>
    </div>
  );
}

export function PeoplePage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const query = params.get("q") ?? "";
  const groupId = params.get("group") ?? "";
  const starred = params.get("starred") === "1";
  const sort = (params.get("sort") as ContactSort | null) ?? "name";
  const [panel, setPanel] = useState<PeopleFilter>(() => emptyFilter());
  const [limit, setLimit] = useState(PAGE);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [focused, setFocused] = useState(-1);
  const [bulk, setBulk] = useState<"group" | "merge" | "archive" | null>(null);
  const lastClick = useRef<number>(-1);
  const open = useOpenContact();
  const sidebarId = useSelection((st) => st.sidebarContactId);

  const setParam = useCallback((key: string, value: string) => {
    setParams((p) => {
      const n = new URLSearchParams(p);
      if (value) n.set(key, value);
      else n.delete(key);
      return n;
    }, { replace: true });
  }, [setParams]);

  // The panel's clauses and the typed query compose with AND, as on the phone; sort lives in the URL with the rest.
  const filter = useMemo(() => ({ ...panel, sort }), [panel, sort]);
  const groupNames = useGroupNames();
  const parts = useMemo(() => filterParts(filter, groupNames), [filter, groupNames]);
  const composed = composeQuery(parts.map((p) => p.clause).join(" "), query);
  const includeArchived = /\bis_archived\b/.test(composed) || undefined;
  const contacts = useEngineQuery("queryContacts", { query: composed, groupId: groupId || undefined, starredOnly: starred || undefined, includeArchived, sort, direction: sort === "name" ? "asc" : "desc", limit, offset: 0 }, { placeholderData: (prev) => prev });
  const groups = useEngineQuery("listGroups", undefined);
  const chips = useQueryChips({ parts, typed: query, onClearPart: (key) => setPanel((f) => clearPart(f, key)), onReset: () => setPanel(emptyFilter()), onTyped: (q) => setParam("q", q) });
  const onFilter = (f: PeopleFilter) => {
    if (f.sort !== sort) setParam("sort", f.sort === "name" ? "" : f.sort);
    setPanel({ ...f, sort: "name" });
  };
  const star = useEngineMutation("setContactStarred", { invalidate: ["queryContacts", "getContact", "listGroups", "getContactRows"] });
  const archiveMany = useEngineMutation("archiveContact", { invalidate: false });
  const rows = contacts.data?.items ?? [];
  const total = contacts.data?.total ?? 0;
  const focusedRow = focused >= 0 ? rows[focused] ?? null : null;
  const actions = useContactActions(focusedRow?.contactId ?? sidebarId ?? null, { row: focusedRow });

  useEffect(() => setFocused(-1), [composed, groupId, starred, sort]);

  const parentRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({ count: rows.length, getScrollElement: () => parentRef.current, estimateSize: () => 53, overscan: 12 });
  const items = virtualizer.getVirtualItems();
  useEffect(() => {
    const last = items[items.length - 1];
    if (last && last.index >= rows.length - 20 && rows.length < total && !contacts.isFetching) setLimit((l) => l + PAGE);
  }, [items, rows.length, total, contacts.isFetching]);

  useEffect(() => {
    if (focused >= 0) virtualizer.scrollToIndex(focused, { align: "auto" });
  }, [focused, virtualizer]);

  const toggle = useCallback((index: number, shift: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (shift && lastClick.current >= 0) {
        const [a, b] = [Math.min(lastClick.current, index), Math.max(lastClick.current, index)];
        for (let i = a; i <= b; i++) next.add(rows[i].contactId);
      } else {
        const id = rows[index].contactId;
        if (next.has(id)) next.delete(id);
        else next.add(id);
      }
      return next;
    });
    lastClick.current = index;
  }, [rows]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (isTypingTarget(e.target) || e.metaKey || e.ctrlKey || document.querySelector("[role='dialog']")) return;
      if (e.key === "ArrowDown") { e.preventDefault(); setFocused((f) => Math.min(rows.length - 1, f + 1)); }
      else if (e.key === "ArrowUp") { e.preventDefault(); setFocused((f) => Math.max(0, f - 1)); }
      else if (e.key === "Enter" && focused >= 0 && rows[focused]) { e.preventDefault(); open(rows[focused].contactId, { page: e.shiftKey }); }
      else if (e.key === "x" && focused >= 0) { e.preventDefault(); toggle(focused, false); }
      else if (e.key === "a" && e.shiftKey) { e.preventDefault(); setSelected(new Set(rows.map((r) => r.contactId))); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [rows, focused, open, toggle]);

  const selectedIds = useMemo(() => Array.from(selected), [selected]);
  const groupName = groups.data?.find((g) => g.id === groupId)?.name;
  const title = starred ? "Starred" : groupName ? groupName : "People";

  const archiveSelected = async () => {
    for (const id of selectedIds) await archiveMany.mutateAsync({ contactId: id });
    toast(`Archived ${selectedIds.length} people`);
    setSelected(new Set());
    setBulk(null);
    contacts.refetch();
  };

  return (
    <Page
      title={title}
      flush
      actions={
        <>
          <span className="u-small u-muted">{contacts.isFetching && !contacts.data ? "" : `${total.toLocaleString()} ${total === 1 ? "person" : "people"}`}</span>
          <Button variant="primary" size="sm" icon={<Plus size={14} />} onClick={() => navigate("/people/new")}>
            New person
          </Button>
        </>
      }
    >
      <ChipRow chips={chips.chips} onRemove={chips.remove} onClear={chips.clear} />
      <NeedsOffersBanner />
      <div className={s.toolbar}>
        <SearchBox value={query} onChange={(q) => setParam("q", q)} showParsed={false} />
        <Select value={groupId} onChange={(e) => setParam("group", e.target.value)} aria-label="Filter by group" style={{ width: 170 }}>
          <option value="">All groups</option>
          {(groups.data ?? []).map((g) => (
            <option key={g.id} value={g.id}>{g.name} ({g.memberCount})</option>
          ))}
        </Select>
        <Menu
          align="right"
          trigger={<Button variant="outline" icon={<ArrowDownUp size={14} />}>{SORTS.find((x) => x.id === sort)?.label ?? "Sort"}</Button>}
          items={SORTS.map((x) => ({ id: x.id, label: x.label, onSelect: () => setParam("sort", x.id === "name" ? "" : x.id) }))}
        />
        <FilterPanel filter={filter} onChange={onFilter} count={total} />
        {starred && <Button variant="ghost" size="sm" icon={<X size={12} />} onClick={() => setParam("starred", "")}>Starred only</Button>}
      </div>
      {selectedIds.length > 0 && (
        <div className={s.bulkBar} role="toolbar" aria-label="Bulk actions">
          <strong>{selectedIds.length} selected</strong>
          <Button size="sm" icon={<Tag size={13} />} onClick={() => setBulk("group")}>Add to group</Button>
          <Button size="sm" icon={<Archive size={13} />} onClick={() => setBulk("archive")}>Archive</Button>
          <Button size="sm" icon={<Merge size={13} />} disabled={selectedIds.length < 2 || selectedIds.length > 6} onClick={() => setBulk("merge")} title="Select two to six people to merge">Merge</Button>
          <span className="u-grow" />
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>Clear</Button>
        </div>
      )}
      <div className={s.listHeader} data-compact={sidebarId ? "true" : undefined}>
        <span style={{ width: 24 }} />
        <span style={{ width: 36 }} />
        <span style={{ flex: 1.4 }}>Name</span>
        <span style={{ flex: 1 }}>Location</span>
        <span style={{ width: 120, textAlign: "right" }}>Last interaction</span>
        <span style={{ width: 28 }} />
      </div>
      <div ref={parentRef} style={{ flex: 1, overflowY: "auto" }} role="grid" aria-rowcount={total} tabIndex={-1}>
        {contacts.isLoading && (
          <div style={{ padding: 16 }} className="u-col">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="u-row" style={{ height: 44 }}>
                <span className={s.skeleton} style={{ width: 34, height: 34, borderRadius: 17 }} />
                <span className={s.skeleton} style={{ width: `${30 + (i % 4) * 10}%` }} />
              </div>
            ))}
          </div>
        )}
        {contacts.isSuccess && rows.length === 0 && (
          <EmptyState icon={<Users size={28} className="u-muted" />} title={composed ? "No one matches that search" : "No people yet"} action={!composed ? <Button variant="primary" onClick={() => navigate("/settings/import")}>Import people</Button> : undefined}>
            {composed ? "Try fewer terms, or a different keyword such as company: or location:." : "Connect a source or import a CSV and your people will show up here."}
          </EmptyState>
        )}
        <div style={{ height: virtualizer.getTotalSize(), position: "relative" }}>
          {items.map((vi) => {
            const row = rows[vi.index];
            return (
              <ContactRowView
                key={row.contactId}
                row={row}
                compact={!!sidebarId}
                selected={selected.has(row.contactId)}
                active={sidebarId === row.contactId}
                focused={focused === vi.index}
                style={{ position: "absolute", top: 0, left: 0, width: "100%", transform: `translateY(${vi.start}px)` }}
                onOpen={() => { setFocused(vi.index); open(row.contactId); }}
                onToggle={(shift) => toggle(vi.index, shift)}
                onStar={() => star.mutate({ contactId: row.contactId, starred: !row.isStarred })}
                onDragStart={(e) => {
                  const ids = selected.has(row.contactId) ? selectedIds : [row.contactId];
                  e.dataTransfer.setData(CONTACT_DRAG_TYPE, ids.join(","));
                  e.dataTransfer.effectAllowed = "copy";
                }}
              />
            );
          })}
        </div>
        {contacts.isFetching && contacts.data && rows.length < total && <div style={{ display: "flex", justifyContent: "center", padding: 12 }}><Spinner /></div>}
      </div>
      {actions.modals}
      <GroupPickerModal open={bulk === "group"} onClose={() => { setBulk(null); setSelected(new Set()); }} contactIds={selectedIds} />
      <MergeModal open={bulk === "merge"} onClose={() => { setBulk(null); setSelected(new Set()); }} contactIds={selectedIds} />
      <ConfirmModal open={bulk === "archive"} title={`Archive ${selectedIds.length} people?`} body="They leave your lists and suggestions. Restore any of them from Archive." confirmLabel="Archive" danger loading={archiveMany.isPending} onClose={() => setBulk(null)} onConfirm={archiveSelected} />
    </Page>
  );
}
